import type { Control } from 'react-hook-form'
import { useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, HStack, Input } from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { planCreationTimeHintKey } from '~/models/workflowSection'
import {
  isPlanCreationTimeFieldShown,
  resolvePlanCreationTime,
} from '~/pages/AdminWorkflowTemplates/models/planCreationTime'
import type { WorkflowTemplateFormValues } from '~/pages/AdminWorkflowTemplates/models/schema'

type Props = {
  automationAvailable: boolean
  bigStepIndex: number
  control: Control<WorkflowTemplateFormValues>
  subIndex: number
}

/**
 * 計画を作る時刻欄 (spec AC-WFT-048 / AC-WFT-049)。表示条件は resolver
 * (`models/planCreationTime.ts`) が決め、flag はここで表示にだけ使う。
 * 打った文字列は `planCreationTimeInput` に生のまま (日数と時刻の組で) 書き、
 * 検証は schema が行う。片方を打つ時、もう片方は表示中の文字列を組に入れる。
 */
export const SubStepPlanCreationTimeField = ({
  automationAvailable,
  bigStepIndex,
  control,
  subIndex,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const path = `bigSteps.${bigStepIndex}.subSteps.${subIndex}` as const
  const row = useWatch({ control, name: path })
  const section = useWatch({ control, name: 'section' })
  const input = useController({
    control,
    name: `${path}.planCreationTimeInput`,
  })

  const resolution = resolvePlanCreationTime(row)
  if (resolution.kind === 'notHolder') return null
  if (!isPlanCreationTimeFieldShown(resolution, automationAvailable))
    return null

  const hintKey = planCreationTimeHintKey(section)
  const inputId = `plan-creation-time-${bigStepIndex}-${subIndex}`
  const label = t('page:adminWorkflowTemplates.subStep.planCreationTime')
  const daysLabel = t(
    'page:adminWorkflowTemplates.subStep.planCreationTimeDays',
  )
  const timeLabel = t(
    'page:adminWorkflowTemplates.subStep.planCreationTimeTime',
  )
  const errorId = `${inputId}-error`
  const invalid = input.fieldState.invalid
  const describedBy = invalid ? errorId : undefined
  return (
    <Box paddingLeft="32px">
      <FieldRoot width="full" maxWidth="560px" invalid={invalid}>
        <FieldLabel htmlFor={`${inputId}-days`}>{label}</FieldLabel>
        <HStack alignItems="flex-start" gap="16px">
          <FieldRoot width="auto" invalid={invalid}>
            <FieldLabel htmlFor={`${inputId}-days`}>{daysLabel}</FieldLabel>
            <Input
              ref={input.field.ref}
              width="120px"
              aria-describedby={describedBy}
              aria-label={daysLabel}
              id={`${inputId}-days`}
              inputMode="numeric"
              name={`${input.field.name}.days`}
              onBlur={input.field.onBlur}
              onChange={(e) => {
                input.field.onChange({
                  days: e.target.value,
                  time: resolution.time,
                })
              }}
              value={resolution.days}
            />
          </FieldRoot>
          <FieldRoot width="auto" invalid={invalid}>
            <FieldLabel htmlFor={`${inputId}-time`}>{timeLabel}</FieldLabel>
            <Input
              width="120px"
              aria-describedby={describedBy}
              aria-label={timeLabel}
              id={`${inputId}-time`}
              name={`${input.field.name}.time`}
              onBlur={input.field.onBlur}
              onChange={(e) => {
                input.field.onChange({
                  days: resolution.days,
                  time: e.target.value,
                })
              }}
              placeholder="HH:mm"
              value={resolution.time}
            />
          </FieldRoot>
        </HStack>
        <FieldErrorText id={errorId}>
          {input.fieldState.error?.message}
        </FieldErrorText>
        {hintKey !== undefined && (
          <FieldHelperText color="Text.midEmphasis">
            {t(hintKey)}
          </FieldHelperText>
        )}
      </FieldRoot>
    </Box>
  )
}
